/**
 * Caregiver Portal - Enhanced with Care Plan Tasks and Signatures
 * Features:
 * - Schedule view with clock in/out
 * - Care plan tasks with checkboxes during active visit
 * - PCW and Client signature capture at clock-out
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { SignaturePad } from "../components/SignaturePad";
import {
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  User,
  Navigation,
  LogIn,
  LogOut,
  RefreshCw,
  Calendar,
  Loader2,
  ClipboardList,
  FileText,
  Phone,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  PenTool,
  Smartphone,
  ArrowRight,
  KeyRound,
  ShieldCheck,
  ListChecks,
} from "lucide-react";
import MyDocumentsSection from "../components/MyDocumentsSection";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CaregiverPortalPage() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [activeVisit, setActiveVisit] = useState(null);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [myClients, setMyClients] = useState([]);
  const [notes, setNotes] = useState("");
  const [servicesProvided, setServicesProvided] = useState("");
  const [showClockOut, setShowClockOut] = useState(false);
  const [quickClockInClient, setQuickClockInClient] = useState(null);
  
  // Roles that are primarily office-based but can do field visits
  const officeRolesWithFieldAccess = ['rn', 'case_manager', 'admin'];
  const canAccessDashboard = officeRolesWithFieldAccess.includes(user?.role?.toLowerCase());
  
  // Care Plan state
  const [carePlan, setCarePlan] = useState(null);
  const [completedTasks, setCompletedTasks] = useState({});
  const [showCarePlan, setShowCarePlan] = useState(true);
  
  // Signature state
  const [pcwSignature, setPcwSignature] = useState(null);
  const [clientSignature, setClientSignature] = useState(null);
  const [clientSignedBy, setClientSignedBy] = useState("");

  // Incomplete-task warning state
  const [showTaskWarning, setShowTaskWarning] = useState(false);
  const [incompleteTaskNames, setIncompleteTaskNames] = useState([]);

  // Client hand-off state (pass phone to client to sign)
  const [showClientHandoff, setShowClientHandoff] = useState(false);

  // Care Plan Auto-Save state ("idle" | "saving" | "saved" | "error")
  const [taskAutoSaveState, setTaskAutoSaveState] = useState("idle");

  // Client PIN state (for hand-off)
  const [clientPinAvailable, setClientPinAvailable] = useState(false);
  const [pinEntryMode, setPinEntryMode] = useState(false);
  const [pinDigits, setPinDigits] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [signatureRefreshNeeded, setSignatureRefreshNeeded] = useState(false);

  // Fetch active visit
  const fetchActiveVisit = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/evv/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveVisit(data);
        // Fetch care plan for this client
        if (data?.client_id) {
          fetchCarePlan(data.client_id);
        }
      } else {
        setActiveVisit(null);
        setCarePlan(null);
      }
    } catch (err) {
      setActiveVisit(null);
    }
  }, [token]);

  // Fetch care plan for a client
  const fetchCarePlan = async (clientId) => {
    try {
      const res = await fetch(`${API}/api/evv/client-care-plan/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCarePlan(data);
        // Initialize completed tasks state
        const initial = {};
        data.tasks?.forEach(task => {
          initial[task.id] = false;
        });
        setCompletedTasks(initial);
      }
    } catch (err) {
      console.error("Failed to fetch care plan:", err);
    }
  };

  // Fetch today's schedule
  const fetchSchedule = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/evv/my-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTodaySchedule(data);
      }
    } catch (err) {
      console.error("Failed to fetch schedule:", err);
    }
  }, [token]);

  // Fetch my clients for quick clock-in
  const fetchMyClients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/evv/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyClients(data);
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  }, [token]);

  // Get current GPS location
  const getCurrentLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
      },
      (error) => {
        setLocationError(
          error.code === 1 ? "Location access denied. Please enable GPS." :
          error.code === 2 ? "Location unavailable. Please try again." :
          "Unable to get location. Please try again."
        );
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchActiveVisit(),
        fetchSchedule(),
        fetchMyClients()
      ]);
      getCurrentLocation();
      setLoading(false);
    };
    loadData();
  }, [fetchActiveVisit, fetchSchedule, fetchMyClients, getCurrentLocation]);

  // Load persisted task progress + client PIN status whenever an active visit appears
  useEffect(() => {
    if (!activeVisit?.id) return;
    let cancelled = false;

    const loadProgress = async () => {
      try {
        const [tp, pin] = await Promise.all([
          axios.get(`${API}/evv/visits/${activeVisit.id}/task-progress`),
          activeVisit.client_id
            ? axios.get(`${API}/evv/clients/${activeVisit.client_id}/signature-pin-status`)
            : Promise.resolve({ data: { has_pin: false } }),
        ]);
        if (cancelled) return;
        const map = {};
        (tp.data.tasks || []).forEach((t) => {
          if (t.completed) map[t.task_id] = true;
        });
        if (Object.keys(map).length) {
          setCompletedTasks((prev) => ({ ...map, ...prev }));
        }
        setClientPinAvailable(!!pin.data.has_pin);
      } catch (err) {
        console.error("Could not load persisted progress:", err);
      }
    };
    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [activeVisit?.id, activeVisit?.client_id]);

  // Clock In
  const handleClockIn = async (appointmentId, clientId) => {
    if (!location) {
      toast.error("Location required. Please enable GPS and try again.");
      getCurrentLocation();
      return;
    }

    setClockingIn(true);
    try {
      const res = await fetch(`${API}/api/evv/clock-in`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointment_id: appointmentId,
          location,
          notes
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        setActiveVisit(data);
        setNotes("");
        
        // Fetch care plan for this client
        if (clientId) {
          fetchCarePlan(clientId);
        }
        
        if (data.location_verification?.verified) {
          toast.success("Clocked in! Location verified.");
        } else {
          toast.warning("Clocked in. Location could not be verified.");
        }
        
        fetchSchedule();
      } else {
        toast.error(data.detail || "Failed to clock in");
      }
    } catch (err) {
      toast.error("Failed to clock in. Please try again.");
    }
    setClockingIn(false);
  };

  // Quick Clock In for unscheduled visit
  const handleQuickClockIn = async (client) => {
    if (!location) {
      toast.error("Location required. Please enable GPS and try again.");
      getCurrentLocation();
      return;
    }

    setQuickClockInClient(client.id);
    try {
      const res = await fetch(`${API}/api/evv/quick-clock-in`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_id: client.id,
          location,
          notes
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        setActiveVisit(data);
        setNotes("");
        
        // Fetch care plan for this client
        fetchCarePlan(client.id);
        
        if (data.clock_in_verified) {
          toast.success(`Clocked in with ${client.first_name}! Location verified.`);
        } else {
          toast.warning(`Clocked in with ${client.first_name}. Location could not be verified.`);
        }
        
        fetchSchedule();
      } else {
        toast.error(data.detail || "Failed to clock in");
      }
    } catch (err) {
      toast.error("Failed to clock in. Please try again.");
    }
    setQuickClockInClient(null);
  };

  // Toggle task completion (with auto-save if a visit is active)
  const toggleTask = (taskId) => {
    setCompletedTasks((prev) => {
      const next = { ...prev, [taskId]: !prev[taskId] };
      autoSaveTask(taskId, next[taskId]);
      return next;
    });
  };

  // Debounced auto-save for a single task tick
  const autoSaveTask = async (taskId, completed) => {
    if (!activeVisit?.id) return;
    const taskName = carePlan?.tasks?.find((t) => t.id === taskId)?.name;
    try {
      setTaskAutoSaveState("saving");
      await axios.post(`${API}/evv/visits/${activeVisit.id}/task-progress`, {
        task_id: taskId,
        task_name: taskName,
        completed,
      });
      setTaskAutoSaveState("saved");
      setTimeout(() => setTaskAutoSaveState("idle"), 2000);
    } catch (err) {
      console.error("Task auto-save failed:", err);
      setTaskAutoSaveState("error");
      setTimeout(() => setTaskAutoSaveState("idle"), 5000);
    }
  };

  // Clock Out
  const handleClockOut = async (skipTaskCheck = false) => {
    if (!activeVisit) return;

    if (!location) {
      toast.error("Location required. Please enable GPS and try again.");
      getCurrentLocation();
      return;
    }

    // Validate signatures
    if (!pcwSignature) {
      toast.error("PCW signature is required");
      return;
    }

    if (!clientSignature) {
      toast.error("Client signature is required");
      return;
    }

    // Warn about incomplete care plan tasks (only once — user can confirm to proceed)
    if (!skipTaskCheck && carePlan?.tasks?.length) {
      const incomplete = carePlan.tasks.filter((t) => !completedTasks[t.id]);
      if (incomplete.length > 0) {
        setIncompleteTaskNames(incomplete.map((t) => t.name));
        setShowTaskWarning(true);
        return;
      }
    }

    setClockingOut(true);
    try {
      // Build completed tasks array
      const tasksToSubmit = carePlan?.tasks?.map(task => ({
        id: task.id,
        name: task.name,
        completed: completedTasks[task.id] || false
      })) || [];

      const res = await fetch(`${API}/api/evv/clock-out`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          visit_id: activeVisit.id,
          location,
          notes: notes || undefined,
          services_provided: servicesProvided || undefined,
          completed_tasks: tasksToSubmit,
          pcw_signature: pcwSignature,
          client_signature: clientSignature,
          client_signed_by: clientSignedBy || undefined
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Clocked out! Visit duration: ${data.duration_minutes} minutes`);
        setActiveVisit(null);
        setCarePlan(null);
        setShowClockOut(false);
        setNotes("");
        setServicesProvided("");
        setCompletedTasks({});
        setPcwSignature(null);
        setClientSignature(null);
        setClientSignedBy("");
        fetchSchedule();
      } else {
        toast.error(data.detail || "Failed to clock out");
      }
    } catch (err) {
      toast.error("Failed to clock out. Please try again.");
    }
    setClockingOut(false);
  };

  // Format time
  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate elapsed time
  const getElapsedTime = (startTime) => {
    if (!startTime) return "";
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Get today's date formatted
  const getTodayDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-slate-600">Loading your schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white pb-24" id="caregiver-portal-root">
      {/* Header */}
      <div className="bg-teal-700 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back to Dashboard button for office roles */}
            {canAccessDashboard && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-teal-600 -ml-2"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            )}
            <div>
              <h1 className="text-lg font-bold">
                {canAccessDashboard ? "Field Mode" : "My Schedule"}
              </h1>
              <p className="text-teal-100 text-sm">{user?.name || "Caregiver"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                fetchActiveVisit();
                fetchSchedule();
                fetchMyClients();
                getCurrentLocation();
              }}
              className="text-white hover:bg-teal-600"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {!canAccessDashboard && (
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-white hover:bg-teal-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Today's Date */}
        <p className="text-sm text-slate-500 text-center">{getTodayDate()}</p>

        {/* GPS Status */}
        <Card className={`${location ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              {gettingLocation ? (
                <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
              ) : location ? (
                <MapPin className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600" />
              )}
              <div className="flex-1">
                {gettingLocation ? (
                  <p className="text-sm text-slate-600">Getting your location...</p>
                ) : location ? (
                  <p className="text-sm text-green-700">
                    GPS Active • Accuracy: {Math.round(location.accuracy)}m
                  </p>
                ) : (
                  <p className="text-sm text-amber-700">{locationError || "Location unavailable"}</p>
                )}
              </div>
              {!location && !gettingLocation && (
                <Button size="sm" variant="outline" onClick={getCurrentLocation}>
                  <Navigation className="w-4 h-4 mr-1" /> Enable
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Active Visit Card - Show when clocked in */}
        {activeVisit && (
          <>
            <Card className="border-teal-300 bg-teal-50" id="section-active">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-600" />
                    Currently Clocked In
                  </CardTitle>
                  <Badge className="bg-teal-600 text-white">{getElapsedTime(activeVisit.clock_in_time)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">{activeVisit.client_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <LogIn className="w-4 h-4" />
                  Clocked in at {formatTime(activeVisit.clock_in_time)}
                </div>
                {activeVisit.clock_in_verified !== null && (
                  <div className="flex items-center gap-2 text-sm">
                    {activeVisit.clock_in_verified ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-700">Location verified</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="text-amber-700">Location not verified</span>
                      </>
                    )}
                  </div>
                )}
                
                {!showClockOut && (
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 mt-2"
                    onClick={() => setShowClockOut(true)}
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Clock Out
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Client Information Card - Show when clocked in */}
            {carePlan && (
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    Client Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Name</p>
                      <p className="font-medium text-slate-900">
                        {carePlan.client_info?.name || activeVisit.client_name}
                        {carePlan.client_info?.preferred_name && (
                          <span className="text-purple-600 ml-1">"{carePlan.client_info.preferred_name}"</span>
                        )}
                      </p>
                    </div>
                    {carePlan.client_info?.date_of_birth && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Date of Birth</p>
                        <p className="font-medium text-slate-900">{carePlan.client_info.date_of_birth}</p>
                      </div>
                    )}
                  </div>

                  {/* Contact Info */}
                  {(carePlan.client_info?.home_phone || carePlan.client_info?.cell_phone || carePlan.client_info?.address) && (
                    <div className="bg-white rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Contact</p>
                      {carePlan.client_info?.address && (
                        <p className="text-sm text-slate-700 flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                          {carePlan.client_info.address}
                        </p>
                      )}
                      {carePlan.client_info?.home_phone && (
                        <p className="text-sm text-slate-700 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          Home: {carePlan.client_info.home_phone}
                        </p>
                      )}
                      {carePlan.client_info?.cell_phone && (
                        <p className="text-sm text-slate-700 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          Cell: {carePlan.client_info.cell_phone}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Emergency Contact */}
                  {carePlan.emergency_contact?.name && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-red-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Emergency Contact
                      </p>
                      <p className="font-medium text-red-800">{carePlan.emergency_contact.name}</p>
                      {carePlan.emergency_contact.relationship && (
                        <p className="text-sm text-red-700">{carePlan.emergency_contact.relationship}</p>
                      )}
                      {carePlan.emergency_contact.phone && (
                        <p className="text-sm text-red-700 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {carePlan.emergency_contact.phone}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Health & Safety Info */}
                  {(carePlan.health_info?.allergies || carePlan.health_info?.precautions || carePlan.health_info?.diagnosis || carePlan.health_info?.code_status) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Health & Safety Information
                      </p>
                      {carePlan.health_info.allergies && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Allergies:</p>
                          <p className="text-sm text-amber-800">{carePlan.health_info.allergies}</p>
                        </div>
                      )}
                      {carePlan.health_info.precautions && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Precautions:</p>
                          <p className="text-sm text-amber-800">{carePlan.health_info.precautions}</p>
                        </div>
                      )}
                      {carePlan.health_info.diagnosis && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Diagnosis:</p>
                          <p className="text-sm text-amber-800">{carePlan.health_info.diagnosis}</p>
                        </div>
                      )}
                      {carePlan.health_info.code_status && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Code Status:</p>
                          <p className="text-sm text-amber-800 font-medium">{carePlan.health_info.code_status}</p>
                        </div>
                      )}
                      {carePlan.health_info.pets && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Pets:</p>
                          <p className="text-sm text-amber-800">{carePlan.health_info.pets}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Schedule Info */}
                  {carePlan.schedule_info?.time_of_visits && (
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Scheduled Visit Time</p>
                      <p className="font-medium text-slate-900">{carePlan.schedule_info.time_of_visits}</p>
                      {carePlan.schedule_info.hours_per_week && (
                        <p className="text-sm text-slate-600">{carePlan.schedule_info.hours_per_week} hours/week</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Care Plan Tasks - Show during active visit */}
            {carePlan?.has_care_plan && carePlan.tasks?.length > 0 && (
              <Card className="border-blue-200" id="section-careplan">
                <CardHeader className="pb-2">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setShowCarePlan(!showCarePlan)}
                  >
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-blue-600" />
                      Today&apos;s Care Plan Tasks
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {taskAutoSaveState !== "idle" && (
                        <span
                          className={`text-xs flex items-center gap-1 ${
                            taskAutoSaveState === "saving"
                              ? "text-slate-500"
                              : taskAutoSaveState === "saved"
                              ? "text-emerald-600"
                              : "text-red-600"
                          }`}
                          data-testid="task-autosave-indicator"
                        >
                          {taskAutoSaveState === "saving" && <Loader2 className="w-3 h-3 animate-spin" />}
                          {taskAutoSaveState === "saved" && <CheckCircle className="w-3 h-3" />}
                          {taskAutoSaveState === "error" && <AlertCircle className="w-3 h-3" />}
                          {taskAutoSaveState === "saving"
                            ? "Saving..."
                            : taskAutoSaveState === "saved"
                            ? "Saved"
                            : "Retry"}
                        </span>
                      )}
                      <Badge variant="outline" className="bg-blue-50">
                        {Object.values(completedTasks).filter(Boolean).length} / {carePlan.tasks.length}
                      </Badge>
                      {showCarePlan ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                </CardHeader>
                {showCarePlan && (
                  <CardContent className="space-y-4">
                    {/* Task Checklist */}
                    <div className="space-y-2">
                      {carePlan.tasks.map((task) => (
                        <div 
                          key={task.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            completedTasks[task.id] 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-white border-slate-200 hover:border-blue-200'
                          }`}
                        >
                          <Checkbox
                            id={task.id}
                            checked={completedTasks[task.id] || false}
                            onCheckedChange={() => toggleTask(task.id)}
                            className="mt-0.5"
                          />
                          <label 
                            htmlFor={task.id} 
                            className="flex-1 cursor-pointer"
                          >
                            <p className={`font-medium ${completedTasks[task.id] ? 'text-green-700 line-through' : 'text-slate-900'}`}>
                              {task.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {task.assistance_type} • {task.frequency}
                            </p>
                          </label>
                          {completedTasks[task.id] && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Clock Out Form with Signatures */}
            {showClockOut && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <PenTool className="w-5 h-5" />
                    Complete Visit & Clock Out
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Services Summary */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Services Provided
                    </label>
                    <Textarea
                      placeholder="Describe services provided during this visit..."
                      value={servicesProvided}
                      onChange={(e) => setServicesProvided(e.target.value)}
                      rows={2}
                    />
                  </div>
                  
                  {/* Additional Notes */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Additional Notes
                    </label>
                    <Textarea
                      placeholder="Any observations, concerns, or notes about the visit..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                    />
                  </div>

                  {/* PCW Signature */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      PCW Signature (Required)
                    </label>
                    <SignaturePad
                      onSave={setPcwSignature}
                      onClear={() => setPcwSignature(null)}
                      width={350}
                      height={120}
                    />
                    {pcwSignature && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Signature captured
                      </p>
                    )}
                  </div>

                  {/* Client Signature - captured via hand-off */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Client Signature (Required)
                    </label>
                    {clientSignature ? (
                      <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium text-emerald-800">Client signature captured</p>
                            {clientSignedBy && (
                              <p className="text-xs text-emerald-700">Signed by {clientSignedBy}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setClientSignature(null);
                            setClientSignedBy("");
                          }}
                          className="text-red-600"
                          data-testid="reset-client-signature-btn"
                        >
                          Re-sign
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        className="w-full h-14 bg-purple-600 hover:bg-purple-700 gap-2 text-base"
                        onClick={() => setShowClientHandoff(true)}
                        disabled={!pcwSignature}
                        data-testid="pass-to-client-btn"
                      >
                        <Smartphone className="w-5 h-5" />
                        Hand phone to client to sign
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                    )}
                    {!pcwSignature && !clientSignature && (
                      <p className="text-xs text-slate-500">
                        Sign yourself first, then pass the phone to the client.
                      </p>
                    )}
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowClockOut(false);
                        setPcwSignature(null);
                        setClientSignature(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={() => handleClockOut(false)}
                      disabled={clockingOut || !location || !pcwSignature || !clientSignature}
                      data-testid="confirm-clockout-btn"
                    >
                      {clockingOut ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <LogOut className="w-4 h-4 mr-2" />
                      )}
                      Confirm Clock Out
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Today's Schedule */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              Today&apos;s Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaySchedule.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No visits scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaySchedule.map((apt) => (
                  <div
                    key={apt.id}
                    className={`p-4 rounded-lg border ${
                      apt.is_completed ? 'bg-green-50 border-green-200' : 
                      apt.is_clocked_in ? 'bg-teal-50 border-teal-200' : 
                      'bg-white border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{apt.client_name}</p>
                          <span className="text-sm font-medium text-teal-700 bg-teal-100 px-2 py-0.5 rounded">
                            {apt.start_time || apt.scheduled_time || "TBD"} - {apt.end_time || "TBD"}
                          </span>
                        </div>
                        {apt.client_address && (
                          <p className="text-xs text-slate-500 mt-1">
                            {typeof apt.client_address === 'object' 
                              ? `${apt.client_address.street}, ${apt.client_address.city}`
                              : apt.client_address
                            }
                          </p>
                        )}
                      </div>
                      <div className="ml-3">
                        {apt.is_completed ? (
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" /> Done
                          </Badge>
                        ) : apt.is_clocked_in ? (
                          <Badge className="bg-teal-600 text-white">In Progress</Badge>
                        ) : activeVisit ? (
                          <Badge variant="outline" className="text-slate-500">Pending</Badge>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => handleClockIn(apt.id, apt.client_id)}
                            disabled={clockingIn || !location}
                          >
                            {clockingIn ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <LogIn className="w-4 h-4 mr-1" /> Clock In
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Clock In - For unscheduled visits */}
        {!activeVisit && myClients.length > 0 && (
          <Card id="section-schedule">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600" />
                Quick Clock In
              </CardTitle>
              <p className="text-sm text-slate-500">For unscheduled visits with your assigned clients</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {myClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-white"
                  >
                    <div>
                      <p className="font-medium">{client.first_name} {client.last_name}</p>
                      {client.address && (
                        <p className="text-xs text-slate-500">
                          {typeof client.address === 'object'
                            ? `${client.address.street || ''}, ${client.address.city || ''}`
                            : client.address
                          }
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickClockIn(client)}
                      disabled={quickClockInClient === client.id || !location}
                    >
                      {quickClockInClient === client.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <LogIn className="w-4 h-4 mr-1" /> Clock In
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Documents (personal ID + certs) */}
        <MyDocumentsSection />
      </div>

      {/* Bottom navigation bar - fixed on all screen sizes */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg"
        data-testid="caregiver-bottom-nav"
      >
        <div className="grid grid-cols-5 max-w-md mx-auto">
          <button
            className="flex flex-col items-center justify-center py-2.5 text-slate-600 hover:bg-slate-50 active:bg-slate-100"
            onClick={() => {
              const el = document.getElementById("section-schedule");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            data-testid="nav-schedule"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs mt-0.5">Schedule</span>
          </button>
          <button
            className={`flex flex-col items-center justify-center py-2.5 hover:bg-slate-50 active:bg-slate-100 ${
              activeVisit ? "text-teal-600" : "text-slate-400"
            }`}
            onClick={() => {
              const el = document.getElementById("section-active");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="nav-active"
          >
            <div className="relative">
              <Clock className="w-5 h-5" />
              {activeVisit && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-pulse" />
              )}
            </div>
            <span className="text-xs mt-0.5">Active</span>
          </button>
          <button
            className="flex flex-col items-center justify-center py-2.5 text-slate-600 hover:bg-slate-50 active:bg-slate-100"
            onClick={() => {
              const el = document.getElementById("section-careplan");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                if (!showCarePlan) setShowCarePlan(true);
              }
            }}
            data-testid="nav-careplan"
          >
            <ListChecks className="w-5 h-5" />
            <span className="text-xs mt-0.5">Care Plan</span>
          </button>
          <button
            className="flex flex-col items-center justify-center py-2.5 text-slate-600 hover:bg-slate-50 active:bg-slate-100"
            onClick={() => {
              const el = document.getElementById("section-mydocs");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="nav-mydocs"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs mt-0.5">My Docs</span>
          </button>
          <button
            className="flex flex-col items-center justify-center py-2.5 text-red-600 hover:bg-red-50 active:bg-red-100"
            onClick={logout}
            data-testid="nav-logout"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-xs mt-0.5">Sign Out</span>
          </button>
        </div>
      </nav>

      {/* Incomplete task warning (mobile-friendly modal) */}
      <AlertDialog open={showTaskWarning} onOpenChange={setShowTaskWarning}>
        <AlertDialogContent data-testid="incomplete-tasks-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Some care plan tasks aren't checked</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-2">
                  {incompleteTaskNames.length} task
                  {incompleteTaskNames.length === 1 ? "" : "s"} on today&apos;s care plan
                  haven&apos;t been marked complete:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1 max-h-40 overflow-y-auto">
                  {incompleteTaskNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                <p className="mt-3 text-sm">
                  Are you sure you want to clock out without marking them?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="back-to-tasks-btn">Go back to tasks</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowTaskWarning(false);
                handleClockOut(true);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="clockout-anyway-btn"
            >
              Clock out anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Client hand-off — full-screen client-facing signature view */}
      {showClientHandoff && (
        <div
          className="fixed inset-0 z-50 bg-white overflow-y-auto"
          data-testid="client-handoff-panel"
        >
          <div className="min-h-full flex flex-col">
            {/* Client-facing header */}
            <div className="bg-purple-700 text-white p-5">
              <div className="flex items-center gap-3">
                <Smartphone className="w-6 h-6" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-purple-200">Now signing</p>
                  <h2 className="text-xl font-bold leading-tight">
                    {carePlan?.client?.first_name || activeVisit?.client_name || "Client"}
                    {carePlan?.client?.last_name ? ` ${carePlan.client.last_name}` : ""}
                  </h2>
                </div>
              </div>
              <p className="text-sm text-purple-100 mt-3">
                Please review the services provided today and sign below to confirm.
              </p>
            </div>

            <div className="flex-1 p-5 space-y-5">
              {/* Visit summary */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <User className="w-4 h-4 text-slate-500" />
                  <span>Caregiver: <span className="font-medium">{user?.name || "Care Worker"}</span></span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span>
                    Started at {formatTime(activeVisit?.clock_in_time)} · Duration {getElapsedTime(activeVisit?.clock_in_time)}
                  </span>
                </div>
                {servicesProvided && (
                  <div className="pt-2 border-t border-slate-200 mt-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Services provided</p>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{servicesProvided}</p>
                  </div>
                )}
                {carePlan?.tasks?.length > 0 && (
                  <div className="pt-2 border-t border-slate-200 mt-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      Care plan tasks completed
                    </p>
                    <ul className="text-sm text-slate-800 space-y-0.5">
                      {carePlan.tasks
                        .filter((t) => completedTasks[t.id])
                        .map((t) => (
                          <li key={t.id} className="flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                            {t.name}
                          </li>
                        ))}
                      {carePlan.tasks.filter((t) => completedTasks[t.id]).length === 0 && (
                        <li className="text-slate-500 italic">No tasks were marked complete.</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Optional signer-name for proxy */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  If someone is signing on behalf of the client, enter their name:
                </label>
                <Input
                  placeholder="e.g. Daughter — Jane Smith (optional)"
                  value={clientSignedBy}
                  onChange={(e) => setClientSignedBy(e.target.value)}
                  data-testid="handoff-signed-by-input"
                />
              </div>

              {/* Signature capture: either drawn OR PIN */}
              {pinEntryMode ? (
                <div className="space-y-3" data-testid="client-pin-entry">
                  <div className="text-center">
                    <p className="text-sm text-slate-700 font-medium mb-1">
                      Enter your 4-digit PIN
                    </p>
                    <p className="text-xs text-slate-500">
                      A caregiver requested confirmation. Please tap your PIN below.
                    </p>
                  </div>
                  <div className="flex justify-center gap-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-mono ${
                          pinDigits.length > i ? "border-purple-500 bg-purple-50" : "border-slate-200"
                        }`}
                      >
                        {pinDigits[i] ? "●" : ""}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                      <Button
                        key={d}
                        variant="outline"
                        className="h-14 text-xl font-mono"
                        onClick={() => setPinDigits((p) => (p.length < 4 ? p + d : p))}
                        data-testid={`pin-key-${d}`}
                      >
                        {d}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      className="h-14"
                      onClick={() => setPinDigits((p) => p.slice(0, -1))}
                      data-testid="pin-key-back"
                    >
                      ←
                    </Button>
                    <Button
                      variant="outline"
                      className="h-14 text-xl font-mono"
                      onClick={() => setPinDigits((p) => (p.length < 4 ? p + "0" : p))}
                      data-testid="pin-key-0"
                    >
                      0
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-14 text-xs"
                      onClick={() => {
                        setPinEntryMode(false);
                        setPinDigits("");
                      }}
                    >
                      Draw instead
                    </Button>
                  </div>
                  <Button
                    className="w-full h-12 bg-purple-600 hover:bg-purple-700"
                    disabled={pinDigits.length !== 4 || verifyingPin}
                    onClick={async () => {
                      setVerifyingPin(true);
                      try {
                        const res = await axios.post(`${API}/evv/verify-client-pin`, {
                          client_id: activeVisit.client_id,
                          pin: pinDigits,
                        });
                        if (res.data.signature_refresh_required) {
                          // Signature on file is stale or missing — must re-draw
                          toast.warning("PIN verified, but the on-file signature has expired. Please have the client sign below to refresh (valid 30 days).");
                          setPinEntryMode(false);
                          setPinDigits("");
                          setClientSignedBy(clientSignedBy || res.data.client_name);
                          // Set a flag so the redraw flow knows to also POST /signature-refresh
                          setSignatureRefreshNeeded(true);
                        } else {
                          // Use the saved signature image directly — it will appear on the visit form
                          setClientSignature(res.data.signature_on_file);
                          setClientSignedBy(clientSignedBy || res.data.client_name);
                          toast.success("PIN verified — signature-on-file applied to visit form");
                          setPinEntryMode(false);
                          setPinDigits("");
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Incorrect PIN");
                        setPinDigits("");
                      } finally {
                        setVerifyingPin(false);
                      }
                    }}
                    data-testid="verify-pin-btn"
                  >
                    {verifyingPin ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify PIN"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">
                      Sign below to confirm:
                    </label>
                    {clientPinAvailable && !signatureRefreshNeeded && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:bg-purple-50 h-7 text-xs gap-1"
                        onClick={() => {
                          setPinEntryMode(true);
                          setPinDigits("");
                        }}
                        data-testid="use-pin-instead-btn"
                      >
                        <KeyRound className="w-3 h-3" />
                        Use PIN instead
                      </Button>
                    )}
                  </div>
                  {signatureRefreshNeeded && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Signature-on-file expired. This drawing will be saved to the client's record for the next 30 days.</span>
                    </div>
                  )}
                  <SignaturePad
                    onSave={setClientSignature}
                    onClear={() => setClientSignature(null)}
                    width={350}
                    height={200}
                  />
                  {clientSignature && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Thank you — signature captured
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Action bar */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setClientSignature(null);
                  setShowClientHandoff(false);
                }}
                data-testid="handoff-cancel-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Return to PCW
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={async () => {
                  // If the on-file signature was expired, save the fresh draw back to the client
                  if (signatureRefreshNeeded && clientSignature && !clientSignature.startsWith("PIN_VERIFIED:")) {
                    try {
                      await axios.post(
                        `${API}/evv/clients/${activeVisit.client_id}/signature-refresh`,
                        { signature_on_file: clientSignature }
                      );
                      toast.success("Signature-on-file refreshed for 30 days");
                    } catch (err) {
                      console.error("Signature refresh failed:", err);
                    }
                    setSignatureRefreshNeeded(false);
                  }
                  setShowClientHandoff(false);
                }}
                disabled={!clientSignature}
                data-testid="handoff-done-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
